/**
 * DemandPointContext
 * Single source of truth for demand points (buildings) data.
 * All building/floor data is derived from GET /api/demand-points.
 *
 * Pagination: backend uses PageQuery (page: 0-based, size: 1-100).
 * PageResponse fields: content, total_elements, total_pages, number, size,
 *                      first, last, number_of_elements, empty.
 */
import React, { createContext, useContext, useState, useCallback } from 'react';
import axios from 'axios';
import { API_ROOT as API } from './config';
import {FLOOR_TYPES} from "./constants/floorTypes";

/**
 * Formats a date to "yyyy-MM-dd'T'HH:mm:ss" pattern.
 * @param {string|Date|null} date
 * @returns {string|null}
 */
export function formatDateToIso(date) {
  if (!date) return null;
  try {
    const d = new Date(date);
    if (isNaN(d.getTime())) return null;
    
    const pad = (num) => String(num).padStart(2, '0');
    
    const YYYY = d.getFullYear();
    const MM = pad(d.getMonth() + 1);
    const DD = pad(d.getDate());
    const HH = pad(d.getHours());
    const mm = pad(d.getMinutes());
    const ss = pad(d.getSeconds());
    
    return `${YYYY}-${MM}-${DD}T${HH}:${mm}:${ss}`;
  } catch (e) {
    return null;
  }
}

const DemandPointContext = createContext();

export const useDemandPoints = () => {
  const ctx = useContext(DemandPointContext);
  if (!ctx) throw new Error('useDemandPoints must be used within DemandPointProvider');
  return ctx;
};

export const PAGE_SIZE = 20; // matches backend default

/**
 * Map a single DemandPointResponseDto to the building shape used by the UI.
 * Real backend response uses camelCase: postalAddress, externalId, externalUuid.
 * There is no top-level "id" field — demand point is identified by externalId.
 */
export function mapDemandPointToBuilding(dp) {
  if (!dp) return null;

  // Demand point identifier — backend uses externalId (number), no "id" field
  const dpId = dp.id ?? dp.externalId ?? dp.external_id ?? null;

  // Postal address — backend sends camelCase "postalAddress", fields inside are snake_case
  const rawAddr = dp.postalAddress ?? dp.postal_address ?? dp.address ?? null;

  // Building data
  const rawBuilding = dp.building ?? {};
  const floors = rawBuilding.floors ?? [];

  // Calculate totals from floors
  const residential_units = floors.reduce(
    (sum, f) => sum + (f.units?.living_unit_count ?? f.units?.livingUnitCount ?? 0), 0
  );
  const business_units = floors.reduce(
    (sum, f) => sum + (f.units?.commercial_unit_count ?? f.units?.commercialUnitCount ?? 0), 0
  );

  // Location
  const rawLoc = dp.location ?? null;

// Last modification date
  const lastModDate = rawBuilding.last_modification_date ?? rawBuilding.lastModificationDate ?? null;

  return {
    // Building fields
    ...rawBuilding,
    id: rawBuilding.id ?? rawBuilding.externalId ?? rawBuilding.external_id ?? dpId,

    // Demand point identifier
    demand_point_id: dpId,

    // Computed unit counts from floors
    residential_units,
    business_units,
    total_floors: floors.filter(f => f.floorType !== FLOOR_TYPES.UNKNOWN.value).length || floors.length,

    // Normalised building type
    building_type:
      rawBuilding.building_type ??
      rawBuilding.buildingType  ??
      dp.type ??
      null,

    // Documents from BuildingDto
    documents: (rawBuilding.documents || []).map(doc => ({
      externalId:           doc.external_id           ?? doc.externalId           ?? null,
      externalDocumentId:   doc.external_document_id   ?? doc.externalDocumentId   ?? null,
      displayName:          doc.display_name          ?? doc.displayName          ?? null,
      fileSize:             doc.file_size             ?? doc.fileSize             ?? 0,
      documentType:         doc.document_type         ?? doc.documentType         ?? null,
      dateOfCreation:       formatDateToIso(doc.date_of_creation       ?? doc.dateOfCreation),
      lastModificationDate: formatDateToIso(doc.last_modification_date ?? doc.lastModificationDate),
      version:              doc.version              ?? 0,
      tags:                 doc.tags                 ?? [],
      buildingDocumentType:                 doc.buildingDocumentType                 ?? null,
    })),

    // Postal address — normalised to snake_case
    address: rawAddr ? {
      street:              rawAddr.street              ?? null,
      house_number:        rawAddr.house_number        ?? rawAddr.houseNumber ?? null,
      house_number_suffix: rawAddr.house_number_suffix ?? rawAddr.houseNumberSuffix ?? null,
      zip_code:            rawAddr.zip_code            ?? rawAddr.zipCode ?? null,
      city:                rawAddr.city                ?? null,
      district:            rawAddr.district            ?? null,
      federal_state:       rawAddr.federal_state       ?? rawAddr.federalState ?? null,
      external_id:         rawAddr.external_id         ?? rawAddr.externalId ?? null,
      version:             rawAddr.version             ?? null,
    } : null,

    // Location
    location: rawLoc ? {
      latitude:  rawLoc.latitude  ?? rawLoc.lat ?? null,
      longitude: rawLoc.longitude ?? rawLoc.lng ?? null,
    } : null,

    // Keep raw demand point for detail view
    _demandPoint: dp,
  };
}

export const DemandPointProvider = ({ children }) => {
  const [demandPoints, setDemandPoints] = useState([]);
  const [buildings, setBuildings] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Server-side pagination metadata from PageResponse
  const [pageInfo, setPageInfo] = useState({
    page: 0,          // current page (0-based)
    size: PAGE_SIZE,  // page size
    totalElements: 0,
    totalPages: 0,
    first: true,
    last: true,
  });

  /** Load all demand points (paginated and filtered). */
  const loadDemandPoints = useCallback(async (page = 0, size = PAGE_SIZE, search_text = null, lat = null, lon = null, radius = null) => {
    setLoading(true);
    setError(null);
    try {
      const params = { page, size };
      if (search_text && search_text.length >= 3) {
        params.search_text = search_text;
      }
      if (lat !== null && lon !== null && radius !== null) {
        params.lat = lat;
        params.lon = lon;
        params.radius = radius;
      }
      
      const response = await axios.get(`${API}/demand-points`, {
        params,
      });

      // Backend returns PageResponse: { content, total_elements, total_pages, ... }
      const data = response.data;
      const content = Array.isArray(data) ? data : data?.content ?? [];

      setDemandPoints(content);
      setBuildings(content.map(mapDemandPointToBuilding).filter(Boolean));

      // Persist pagination metadata
      if (!Array.isArray(data)) {
        setPageInfo({
          page:          data.number          ?? page,
          size:          data.size            ?? size,
          totalElements: data.total_elements  ?? 0,
          totalPages:    data.total_pages     ?? 1,
          first:         data.first           ?? (page === 0),
          last:          data.last            ?? true,
        });
      }

      setError(null);
    } catch (err) {
      console.error('DemandPointContext: failed to load demand points', err);
      setError(err.response?.data?.detail || err.message || 'Failed to load demand points');
      setDemandPoints([]);
      setBuildings([]);
    } finally {
      setLoading(false);
    }
  }, []);

  /** Get a single demand point by id (from local cache first). */
  const getDemandPointById = useCallback(
    async (id) => {
      // Try cache first
      const cached = demandPoints.find((dp) => dp.id === id);
      if (cached) return cached;
      // Fallback: fetch from backend
      try {
        const response = await axios.get(`${API}/demand-points/${id}`);
        return response.data;
      } catch (err) {
        console.error('DemandPointContext: failed to fetch demand point', id, err);
        return null;
      }
    },
    [demandPoints]
  );

  /**
   * Update an existing demand point via PUT /api/demand-points/{id}.
   * NOTE: Backend PUT is currently a stub (returns null). We optimistically
   * update local state so the UI reflects changes without a server round-trip.
   */
  const updateDemandPoint = useCallback(async (id, payload) => {
    try {
      // Import mapper logic from persistence (or we could just use axios here)
      const { saveBuildingWithStableIds } = await import('./building-persistence.js');
      
      // We need to find the building in our local state to get the _demandPoint
      const currentBuilding = buildings.find(b => b.demand_point_id === id);
      const buildingWithContext = {
        ...payload,
        _demandPoint: currentBuilding?._demandPoint
      };

      await saveBuildingWithStableIds(buildingWithContext, currentBuilding);
    } catch (err) {
      console.error('DemandPointContext: updateDemandPoint failed:', err.message);
      // We don't throw here to maintain the optimistic update behavior described in comments
    }
    // Optimistic local update
    setDemandPoints((prev) =>
      prev.map((dp) => (dp.id === id ? { ...dp, ...payload } : dp))
    );
    setBuildings((prev) =>
      prev.map((b) => {
        if (b.demand_point_id !== id) return b;
        const updatedDp = { ...(b._demandPoint || {}), ...payload };
        return mapDemandPointToBuilding(updatedDp) || b;
      })
    );
    return { success: true };
  }, [buildings]);

  const value = {
    demandPoints,
    buildings,
    loading,
    error,
    pageInfo,
    loadDemandPoints,
    getDemandPointById,
    updateDemandPoint,
  };

  return (
    <DemandPointContext.Provider value={value}>
      {children}
    </DemandPointContext.Provider>
  );
};
